import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadState, saveState, clearState } from '../lib/storage.js';
import {
  EMPTY_STREAK,
  isStreakBroken,
  nextMilestone,
  recordVisit,
  STREAK_MILESTONES,
  toDayKey,
} from '../lib/streak.js';
import { levelFromXp, levelTitle, XP_REWARDS } from '../lib/xp.js';
import { FREE_LIMITS } from '../lib/tiers.js';
import { VERSES } from '../data/verses.js';
import { QUESTIONS } from '../data/questions.js';
import { ACTS, CHAPTERS_BY_ID, STORY_TOTAL } from '../data/story.js';

const DEFAULT_STATE = {
  version: 1,
  xp: 0,
  streak: EMPTY_STREAK,
  bookmarks: [], // verse ids
  learning: {}, // verseId -> 'learning' | 'known'
  seen: {}, // verseId -> true, so reveal XP is only awarded once
  quizAnswers: {}, // questionId -> { correct: boolean, day: 'YYYY-MM-DD' }
  story: {}, // chapterId -> { done: true, correct: boolean }
  chatUsage: { day: null, count: 0 }, // free-tier chat allowance, reset daily
  // NB: no `premium` here, deliberately. Entitlement is not progress — it lives
  // on the server, on the account, and is read through useAccount. A flag in
  // this object would be a flag anyone could set from the browser console.
  accountId: null, // whose progress this is, once it belongs to somebody
  // The Daily: the last puzzle played, how it went, and the run of days.
  daily: { day: null, results: [], streak: 0, best: 0, played: 0 },
  // High scores and play counts. `*Runs`/`*Takes` count completed goes and
  // exist to decide when an anonymous player is asked for an account.
  games: { beforeAfterBest: 0, beforeAfterRuns: 0, characterQuizTakes: 0 },
  lastDailyBonus: null, // day key the daily XP was granted for
  celebratedMilestones: [], // streak milestones already rewarded
  settings: {
    ambient: false,
    sfx: true,
    musicBed: true, // bring the pad up behind the narrator on first play
    musicLevel: 0.7,
    voiceLevel: 1,
  },
};

let toastSeq = 0;

/**
 * The single source of truth for everything the player accumulates.
 *
 * State is mirrored into a ref and mutated through `update()`, which computes
 * the next value and hands React a plain object rather than an updater
 * function. That matters for two reasons: actions like "spend a free chat
 * message" need to decide *and report* synchronously, and side effects (toasts)
 * stay out of the reducer, where StrictMode's double-invocation would fire them
 * twice in development.
 */
export function useProgress() {
  const [state, setState] = useState(() => loadState(DEFAULT_STATE));
  const [toasts, setToasts] = useState([]);
  const stateRef = useRef(state);
  const dailyRunRef = useRef(false);

  // Persist. saveState swallows quota/private-mode failures so a blocked write
  // can't break the session — the app just stops remembering.
  useEffect(() => {
    saveState(state);
  }, [state]);

  const pushToast = useCallback((toast) => {
    const id = ++toastSeq;
    setToasts((current) => [...current, { id, ...toast }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  /** Apply a pure transform and return the committed next state. */
  const update = useCallback((transform) => {
    const previous = stateRef.current;
    const next = transform(previous);
    if (next === previous) return previous;
    stateRef.current = next;
    setState(next);
    return next;
  }, []);

  /** Add XP, announcing a level-up if this pushed the player over the line. */
  const addXp = useCallback(
    (amount, reason) => {
      if (!amount) return;
      const before = levelFromXp(stateRef.current.xp).level;
      const next = update((s) => ({ ...s, xp: Math.max(0, s.xp + amount) }));
      const after = levelFromXp(next.xp).level;

      if (reason) pushToast({ tone: 'xp', title: `+${amount} XP`, body: reason });
      if (after > before) {
        pushToast({
          tone: 'level',
          title: `Level ${after} — ${levelTitle(after)}`,
          body: 'Keep going. Your next verse is waiting.',
        });
      }
    },
    [update, pushToast],
  );

  // ── Daily visit: streak, daily bonus, milestone reward ───────────────────
  useEffect(() => {
    if (dailyRunRef.current) return; // once per mount, and idempotent per day
    dailyRunRef.current = true;

    const today = toDayKey();
    const current = stateRef.current;
    const streak = recordVisit(current.streak, today);
    const alreadyBonused = current.lastDailyBonus === today;
    if (streak === current.streak && alreadyBonused) return;

    let xp = current.xp;
    const celebrated = [...current.celebratedMilestones];
    const queued = [];

    if (!alreadyBonused) {
      xp += XP_REWARDS.dailyVisit;
      queued.push({
        tone: 'streak',
        title: streak.current > 1 ? `${streak.current} day streak 🔥` : 'Welcome back',
        body: `+${XP_REWARDS.dailyVisit} XP for showing up today.`,
      });
    }

    const milestone = STREAK_MILESTONES.find(
      (m) => streak.current >= m && !celebrated.includes(m),
    );
    if (milestone) {
      xp += XP_REWARDS.streakMilestone;
      celebrated.push(milestone);
      queued.push({
        tone: 'milestone',
        title: `${milestone} days in a row`,
        body: `+${XP_REWARDS.streakMilestone} XP milestone bonus.`,
      });
    }

    update((s) => ({
      ...s,
      xp,
      streak,
      lastDailyBonus: today,
      celebratedMilestones: celebrated,
    }));

    queued.forEach(pushToast);
  }, [update, pushToast]);

  // ── Verse actions ────────────────────────────────────────────────────────

  const revealVerse = useCallback(
    (verseId) => {
      update((s) =>
        s.seen[verseId]
          ? s
          : {
              ...s,
              seen: { ...s.seen, [verseId]: true },
              xp: s.xp + XP_REWARDS.verseRevealed,
            },
      );
    },
    [update],
  );

  const markVerse = useCallback(
    (verseId, status) => {
      const previous = stateRef.current.learning[verseId];
      if (previous === status) return;

      // Award only on first arrival at a status, so toggling known → learning
      // → known can't mint XP.
      const firstTime = !previous || (previous === 'learning' && status === 'known');
      const reward = !firstTime
        ? 0
        : status === 'known'
          ? XP_REWARDS.verseMarkedKnown
          : XP_REWARDS.verseMarkedLearning;

      const before = levelFromXp(stateRef.current.xp).level;
      const next = update((s) => ({
        ...s,
        learning: { ...s.learning, [verseId]: status },
        xp: s.xp + reward,
      }));

      const after = levelFromXp(next.xp).level;
      if (after > before) {
        pushToast({
          tone: 'level',
          title: `Level ${after} — ${levelTitle(after)}`,
          body: 'Keep going. Your next verse is waiting.',
        });
      }
    },
    [update, pushToast],
  );

  const toggleBookmark = useCallback(
    (verseId) => {
      update((s) => ({
        ...s,
        bookmarks: s.bookmarks.includes(verseId)
          ? s.bookmarks.filter((id) => id !== verseId)
          : [...s.bookmarks, verseId],
      }));
    },
    [update],
  );

  // ── Quiz ─────────────────────────────────────────────────────────────────

  const recordAnswer = useCallback(
    (questionId, correct, xpAward) => {
      const previous = stateRef.current.quizAnswers[questionId];
      // XP only for the first correct answer to a given question.
      const reward = correct && !previous?.correct ? xpAward : 0;

      const before = levelFromXp(stateRef.current.xp).level;
      const next = update((s) => {
        const record = s.quizAnswers[questionId];
        return {
          ...s,
          quizAnswers: {
            ...s.quizAnswers,
            [questionId]: {
              correct,
              day: toDayKey(),
              // Running totals, which is what practice selection grades on:
              // two correct answers retires a question from the rotation.
              seen: (record?.seen ?? 0) + 1,
              right: (record?.right ?? 0) + (correct ? 1 : 0),
            },
          },
          xp: s.xp + reward,
        };
      });

      const after = levelFromXp(next.xp).level;
      if (after > before) {
        pushToast({
          tone: 'level',
          title: `Level ${after} — ${levelTitle(after)}`,
          body: 'Keep going. Your next verse is waiting.',
        });
      }
    },
    [update, pushToast],
  );

  // ── Story ────────────────────────────────────────────────────────────────

  /**
   * Finish a story chapter. XP for completing it, a bonus if the gate question
   * was answered correctly, and a larger one for closing out a whole act.
   * Replaying a chapter you've already done never re-awards anything.
   */
  const completeChapter = useCallback(
    (chapterId, correct) => {
      const chapter = CHAPTERS_BY_ID[chapterId];
      if (!chapter) return;

      const record = stateRef.current.story[chapterId];
      const already = record?.done;
      const reward = already
        ? 0
        : XP_REWARDS.storyChapter + (correct ? XP_REWARDS.storyChapterCorrect : 0);

      const before = levelFromXp(stateRef.current.xp).level;
      const next = update((s) => {
        const previous = s.story[chapterId];
        const attempts = (previous?.attempts ?? 0) + 1;
        return {
          ...s,
          story: {
            ...s.story,
            [chapterId]: {
              done: true,
              attempts,
              // Both flags are sticky: revisiting a chapter can win back the
              // second star, but the third — right the first time — is gone
              // for good once it's missed.
              correct: correct || !!previous?.correct,
              firstTry: previous?.firstTry ?? (correct && attempts === 1),
            },
          },
          xp: s.xp + reward,
        };
      });

      if (already) return;

      // Did that close out the act?
      const act = ACTS[chapter.actIndex];
      const actDone = act.chapters.every((c) => next.story[c.id]?.done);
      let finalXp = next.xp;
      if (actDone) {
        finalXp = update((s) => ({ ...s, xp: s.xp + XP_REWARDS.storyActComplete })).xp;
      }

      pushToast({
        tone: 'story',
        title: actDone ? `${act.title} complete` : chapter.title,
        body: actDone
          ? `+${reward + XP_REWARDS.storyActComplete} XP — that’s the whole act.`
          : `+${reward} XP · ${chapter.takeaway}`,
      });

      const after = levelFromXp(finalXp).level;
      if (after > before) {
        pushToast({
          tone: 'level',
          title: `Level ${after} — ${levelTitle(after)}`,
          body: 'Keep going. Your next verse is waiting.',
        });
      }
    },
    [update, pushToast],
  );

  // ── Chat allowance ───────────────────────────────────────────────────────

  /**
   * Spend one free chat message, keeping the local counter in step with the
   * server's.
   *
   * This is a mirror, not a gate. The server counts the same messages against
   * the same allowance and refuses the sixth one itself — this copy exists so
   * the remaining count can be shown without a round trip, and premium callers
   * skip it entirely.
   */
  const consumeChatMessage = useCallback(() => {
    const current = stateRef.current;
    const today = toDayKey();
    const used = current.chatUsage.day === today ? current.chatUsage.count : 0;
    if (used >= FREE_LIMITS.chatMessagesPerDay) return false;

    update((s) => ({ ...s, chatUsage: { day: today, count: used + 1 } }));
    return true;
  }, [update]);

  /** Free messages left today. Premium is applied by the caller, not here. */
  const chatRemaining = useMemo(() => {
    const today = toDayKey();
    const used = state.chatUsage.day === today ? state.chatUsage.count : 0;
    return Math.max(0, FREE_LIMITS.chatMessagesPerDay - used);
  }, [state.chatUsage]);

  /**
   * Record a finished Daily.
   *
   * The streak continues only from the immediately preceding puzzle. Missing a
   * day ends it — unlike the study streak, which has grace days, because the
   * whole point of a daily is that it happened on the day.
   */
  const recordDaily = useCallback(
    (day, results) => {
      update((s) => {
        if (s.daily.day === day) return s; // already played; never overwrite
        const consecutive = s.daily.day === day - 1;
        const streak = consecutive ? s.daily.streak + 1 : 1;
        return {
          ...s,
          daily: {
            day,
            results,
            streak,
            best: Math.max(s.daily.best ?? 0, streak),
            played: (s.daily.played ?? 0) + 1,
          },
        };
      });
    },
    [update],
  );

  /**
   * Pay out the character quiz, once ever.
   *
   * Guarded on a flag rather than on whether they have a result, because the
   * quiz is meant to be re-taken and passed around — that is the whole point of
   * it — and a reward that fires every time would make ninety seconds of
   * tapping the cheapest XP in the app. Taking it again is free and still fun;
   * it just does not pay twice.
   */
  const recordCharacterQuiz = useCallback(
    (characterId) => {
      if (stateRef.current.games?.characterQuiz) {
        // Still remember the latest result, just do not pay for it again.
        update((s) => ({ ...s, games: { ...s.games, characterResult: characterId } }));
        return;
      }
      const before = levelFromXp(stateRef.current.xp).level;
      const next = update((s) => ({
        ...s,
        xp: s.xp + XP_REWARDS.characterQuiz,
        games: { ...s.games, characterQuiz: 1, characterResult: characterId },
      }));

      pushToast({
        tone: 'story',
        title: 'Result unlocked',
        body: `+${XP_REWARDS.characterQuiz} XP — share it with someone who needs it.`,
      });

      const after = levelFromXp(next.xp).level;
      if (after > before) {
        pushToast({
          tone: 'level',
          title: `Level ${after} — ${levelTitle(after)}`,
          body: 'Keep going. Your next verse is waiting.',
        });
      }
    },
    [update, pushToast],
  );

  /** Record a game run. Only ever moves a high score upward. */
  const recordGame = useCallback(
    (key, value) => {
      update((s) =>
        (s.games?.[key] ?? 0) >= value
          ? s
          : { ...s, games: { ...s.games, [key]: value } },
      );
    },
    [update],
  );

  /**
   * Count a completed go at one of the free games.
   *
   * Only used to decide when to ask an anonymous player for an account, so it
   * is a counter rather than a high score — recordGame keeps the largest value
   * it has seen, which is exactly wrong for counting.
   *
   * Worth being honest about what this is: it lives in this browser, so
   * clearing site data or opening a private window resets it. That is fine.
   * It is a prompt at the right moment, not a lock, and building it as a lock
   * would mean making people sign in before they have seen anything worth
   * signing in for.
   */
  const countPlay = useCallback(
    (key) => {
      update((s) => ({ ...s, games: { ...s.games, [key]: (s.games?.[key] ?? 0) + 1 } }));
    },
    [update],
  );

  // ── Sync ─────────────────────────────────────────────────────────────────

  /**
   * Take up an account on this device: fold in its saved progress, and record
   * whose progress this now is.
   *
   * The merge rule is "whichever side is further along wins", applied per field
   * rather than to the payload as a whole. Someone who studied on their phone
   * and then signed in on a laptop should lose neither side. Everything here
   * only ever moves forward — XP, streaks, verses seen, questions answered —
   * so a maximum (or a union) is both the simplest merge and the right one.
   *
   * Settings are the exception: those are a preference, not an achievement, so
   * the device you are sitting at wins.
   *
   * The merge only happens when what's on this device is *unclaimed* — either
   * nobody has signed in here, or it's the same person coming back. On a shared
   * computer, one person signing out and another signing in must not pour the
   * first person's bookmarks and XP into the second person's account, so there
   * the account's own copy simply replaces what's here.
   */
  const adoptAccount = useCallback(
    (userId, remote) => {
      const mine = stateRef.current.accountId;
      const someoneElses = mine != null && userId != null && mine !== userId;

      if (!remote || typeof remote !== 'object') {
        // Nothing saved yet — a brand new account. Claim this device for them,
        // but only keep what's here if it isn't the last person's.
        update((s) =>
          someoneElses
            ? { ...DEFAULT_STATE, settings: s.settings, accountId: userId }
            : { ...s, accountId: userId },
        );
        return;
      }

      if (someoneElses) {
        update((s) => ({
          ...DEFAULT_STATE,
          ...remote,
          premium: undefined,
          settings: s.settings,
          accountId: userId,
        }));
        return;
      }

      update((s) => {
        const mergedStreak = (() => {
          const theirs = remote.streak;
          if (!theirs || typeof theirs !== 'object') return s.streak;
          // `lastDay` decides which "current" is live: an older record's run
          // may have lapsed since, so its count is not comparable.
          const mine = s.streak;
          const newer = (theirs.lastDay ?? '') > (mine.lastDay ?? '') ? theirs : mine;
          return {
            ...newer,
            longest: Math.max(mine.longest ?? 0, theirs.longest ?? 0),
          };
        })();

        // Per question: keep the record with more attempts, and never lose a
        // correct answer that either side has.
        const quizAnswers = { ...s.quizAnswers };
        for (const [id, theirs] of Object.entries(remote.quizAnswers ?? {})) {
          const mine = quizAnswers[id];
          if (!mine) {
            quizAnswers[id] = theirs;
            continue;
          }
          quizAnswers[id] = {
            ...((theirs.seen ?? 0) > (mine.seen ?? 0) ? theirs : mine),
            seen: Math.max(mine.seen ?? 0, theirs.seen ?? 0),
            right: Math.max(mine.right ?? 0, theirs.right ?? 0),
            correct: !!mine.correct || !!theirs.correct,
            day: (theirs.day ?? '') > (mine.day ?? '') ? theirs.day : mine.day,
          };
        }

        // Per chapter: done stays done, and the stars are sticky both ways.
        const story = { ...s.story };
        for (const [id, theirs] of Object.entries(remote.story ?? {})) {
          const mine = story[id];
          story[id] = mine
            ? {
                done: !!mine.done || !!theirs.done,
                attempts: Math.max(mine.attempts ?? 0, theirs.attempts ?? 0),
                correct: !!mine.correct || !!theirs.correct,
                firstTry: !!mine.firstTry || !!theirs.firstTry,
              }
            : theirs;
        }

        // 'known' outranks 'learning': marking a verse known is progress, and
        // the other device dropping back to learning shouldn't undo it.
        const learning = { ...s.learning };
        for (const [id, theirs] of Object.entries(remote.learning ?? {})) {
          if (learning[id] !== 'known') learning[id] = theirs;
        }

        return {
          ...s,
          xp: Math.max(s.xp, remote.xp ?? 0),
          streak: mergedStreak,
          bookmarks: [...new Set([...s.bookmarks, ...(remote.bookmarks ?? [])])],
          learning,
          seen: { ...remote.seen, ...s.seen },
          quizAnswers,
          story,
          // Take the higher count for today so signing in can't refill an
          // allowance already spent elsewhere.
          chatUsage:
            remote.chatUsage?.day === s.chatUsage.day
              ? {
                  day: s.chatUsage.day,
                  count: Math.max(s.chatUsage.count, remote.chatUsage.count ?? 0),
                }
              : s.chatUsage,
          games: Object.fromEntries(
            [...new Set([...Object.keys(s.games ?? {}), ...Object.keys(remote.games ?? {})])].map(
              (key) => [key, Math.max(s.games?.[key] ?? 0, remote.games?.[key] ?? 0)],
            ),
          ),
          // The Daily: whichever device has played the later puzzle is the
          // one that knows the current state; the best run survives either way.
          daily:
            (remote.daily?.day ?? -1) > (s.daily.day ?? -1)
              ? { ...remote.daily, best: Math.max(s.daily.best ?? 0, remote.daily.best ?? 0) }
              : { ...s.daily, best: Math.max(s.daily.best ?? 0, remote.daily?.best ?? 0) },
          lastDailyBonus:
            (remote.lastDailyBonus ?? '') > (s.lastDailyBonus ?? '')
              ? remote.lastDailyBonus
              : s.lastDailyBonus,
          celebratedMilestones: [
            ...new Set([
              ...s.celebratedMilestones,
              ...(remote.celebratedMilestones ?? []),
            ]),
          ],
          // Settings stay local: this device's volume is about this device.
          settings: s.settings,
          accountId: userId,
        };
      });
    },
    [update],
  );

  // ── Settings & reset ─────────────────────────────────────────────────────

  const setSetting = useCallback(
    (key, value) => {
      update((s) => ({ ...s, settings: { ...s.settings, [key]: value } }));
    },
    [update],
  );

  const resetProgress = useCallback(() => {
    clearState();
    const fresh = { ...DEFAULT_STATE, streak: EMPTY_STREAK };
    stateRef.current = fresh;
    setState(fresh);
    dailyRunRef.current = false;
  }, []);

  // ── Derived values ───────────────────────────────────────────────────────

  const level = useMemo(() => levelFromXp(state.xp), [state.xp]);

  const stats = useMemo(() => {
    const learningValues = Object.values(state.learning);
    const known = learningValues.filter((v) => v === 'known').length;
    const stillLearning = learningValues.filter((v) => v === 'learning').length;
    const answered = Object.values(state.quizAnswers);
    const correct = answered.filter((a) => a.correct).length;

    return {
      known,
      stillLearning,
      versesTotal: VERSES.length,
      versesSeen: Object.keys(state.seen).length,
      bookmarks: state.bookmarks.length,
      questionsAnswered: answered.length,
      questionsTotal: QUESTIONS.length,
      questionsCorrect: correct,
      accuracy: answered.length ? Math.round((correct / answered.length) * 100) : 0,
      chaptersRead: Object.values(state.story).filter((c) => c.done).length,
      chaptersTotal: STORY_TOTAL,
      streakBroken: isStreakBroken(state.streak),
      nextMilestone: nextMilestone(state.streak.current),
    };
  }, [state]);

  return {
    state,
    level,
    stats,
    toasts,
    dismissToast,
    // For announcements that don't come from progress itself — a completed
    // checkout, say. Toasts are the app's one notification surface, so they are
    // reachable from outside this hook rather than duplicated.
    notify: pushToast,
    addXp,
    revealVerse,
    markVerse,
    toggleBookmark,
    recordAnswer,
    completeChapter,
    consumeChatMessage,
    chatRemaining,
    adoptAccount,
    recordDaily,
    recordGame,
    recordCharacterQuiz,
    setSetting,
    countPlay,
    resetProgress,
  };
}
